import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import { SpacesDocument, useUpdateSpaceLocalAttributeMutation } from "#graphql/supergraphHooks";
import {
  type UpdateSpaceLocalAttributeInput,
  type UpdateSpaceLocalAttributeMutation,
  type UpdateSpaceLocalAttributeMutationVariables,
} from "#graphql/supergraphTypes";

export interface UseUpdateSpaceLocalAttributeReturn {
  updateSpaceLocalAttribute: (
    input: UpdateSpaceLocalAttributeInput,
  ) => Promise<UpdateSpaceLocalAttributeMutation["updateSpaceLocalAttribute"] | null>;
  loading: boolean;
  error: ReturnType<typeof useUpdateSpaceLocalAttributeMutation>[1]["error"];
}

/**
 * DV-674 — edit one space-local attribute: `label`, `description`, the select
 * value set, and the requirement flag. `slug` / `valueType` / `referenceTarget`
 * are immutable post-creation. Requires the Data Vault Admin role.
 *
 * Refetches `Spaces`, which is what carries a space's `locals` into the
 * settings screen and the Space editor. The Space-local *grid* is not covered:
 * its datasource fetches `no-cache` through a server-side row model that no
 * Apollo refetch reaches, so `DocumentTypeSettings` purges it via its
 * `refreshToken` on success instead (same as the DV-671 promotion path).
 *
 * Rejects rather than resolving on a server refusal — notably
 * `AttributeOptionInUseError`, which the backend raises when an edit would drop
 * an option a document still references. Callers keep their modal open.
 */
export const useUpdateSpaceLocalAttribute = (
  options?: MutationHookOptions<
    UpdateSpaceLocalAttributeMutation,
    UpdateSpaceLocalAttributeMutationVariables
  >,
): UseUpdateSpaceLocalAttributeReturn => {
  const [mutate, { loading, error }] = useUpdateSpaceLocalAttributeMutation({
    refetchQueries: [SpacesDocument],
    awaitRefetchQueries: true,
    ...options,
  });

  const updateSpaceLocalAttribute = useCallback(
    async (input: UpdateSpaceLocalAttributeInput) => {
      const { data } = await mutate({ variables: { input } });
      return data?.updateSpaceLocalAttribute ?? null;
    },
    [mutate],
  );

  return { updateSpaceLocalAttribute, loading, error };
};
